import { formatUnits, type Address } from "viem";
import {
  chainSettings,
  type ChainType,
  defaultNativeAddress,
  fallbackChainId,
} from "@/constants/chain-specific";
import tokenInfoAbi from "@/constants/abi/tokenInfoAbi";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useReadContract } from "wagmi";
import type { TokenConfigType } from "@/constants/types/currency";
import { DEAD_ADDRESS } from "@/constants/project-specific/tokens";

interface BalanceData {
  name: string;
  symbol: string;
  decimals: number;
  totalSupply: number;
  balance: {
    bigInt: bigint;
    formatted: number;
  };
  allowance: {
    bigInt: bigint;
    formatted: number;
  };
}

export default function useBalances(
  tokens: Address[] | TokenConfigType[],
  account: Address = defaultNativeAddress,
  spender: Address = DEAD_ADDRESS,
  chainId: ChainType = fallbackChainId
) {
  const [balances, setBalances] = useState<Record<Address, BalanceData>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const tokenAddresses = useMemo(() => {
    // only return addresses if tokens is an array of TokenConfigType
    if ((tokens[0] as TokenConfigType)?.address) {
      return tokens.map((token) => (token as TokenConfigType).address) as Address[];
    }
    return tokens as Address[];
  }, [tokens]);

  const { data: tokenDataAllowance, refetch: refetchTokenDataAllowance } = useReadContract({
    address: (chainSettings[chainId] as any)?.tokenInfoContract,
    abi: tokenInfoAbi,
    functionName: "balancesAllowancesOf",
    args: [tokenAddresses, account, spender],
    query: {
      enabled: true,
    },
    chainId,
  });

  const refetch = useCallback(() => {
    refetchTokenDataAllowance();
  }, [refetchTokenDataAllowance]);


  useEffect(() => {
    if (tokenDataAllowance) {
      const combinedData = tokenDataAllowance.reduce((acc, item, index) => {
        acc[tokenAddresses[index]] = {
          name: item.name,
          symbol: item.symbol,
          decimals: item.decimals,
          totalSupply: Number(formatUnits(item.totalSupply, item.decimals)),
          balance: {
            bigInt: item.balance,
            formatted: Number.parseFloat(formatUnits(item.balance, item.decimals)),
          },
          allowance: {
            bigInt: "allowance" in item ? (item.allowance as bigint) : BigInt(0),
            formatted: "allowance" in item
              ? Number.parseFloat(formatUnits(item.allowance as bigint, item.decimals))
              : 0,
          },
        };
        return acc;
      }, {} as Record<Address, BalanceData>);
      setBalances(combinedData);
      setIsLoading(false);
    }
  }, [tokenDataAllowance]);

  return {
    balances,
    refresh: refetch,
    isLoading,
  };
}